import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import API from '../utils/api';
import { trackSharedStoryView, trackSharedStoryReaction, trackSharedStoryReadAloud } from '../utils/analytics';
import { copyToClipboard } from '../utils/clipboard';

const SPEECH_LANG = {
  en:'en-GB', es:'es-ES', fr:'fr-FR', hi:'hi-IN',
  gu:'gu-IN', de:'de-DE', pt:'pt-BR', ar:'ar-SA',
  zh:'zh-CN', ja:'ja-JP', it:'it-IT',
};

// iOS Safari's speechSynthesis.pause() is broken — it has no effect and speech continues.
// Detect iOS so we can use cancel+restart instead of native pause/resume.
const isIOSSpeech = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const SharedStoryPage = () => {
  const { token } = useParams();
  const [story, setStory] = useState(null);
  const [reactions, setReactions] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | password | expired | error
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [reacted, setReacted] = useState(false);
  const [reactingEmoji, setReactingEmoji] = useState(null);
  const [fontSize, setFontSize] = useState(18);
  const [speaking, setSpeaking] = useState(false);
  const [speechPaused, setSpeechPaused] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [activeParagraph, setActiveParagraph] = useState(-1);
  const [copied, setCopied] = useState(false);
  const [speechError, setSpeechError] = useState(null);
  const utteranceRef = useRef(null);
  const resumeIntervalRef = useRef(null);
  const voicesRef = useRef([]);
  const speechParaPosRef = useRef([]);
  const speechLastCharRef = useRef(0);    // absolute charIndex at last boundary event
  const speechTextOffsetRef = useRef(0);  // offset when speech was restarted (iOS resume)
  const speechFullTextRef = useRef('');   // full clean text, kept for iOS slice-on-resume

  // Pre-load voices so they're available synchronously when user taps (mobile requirement)
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const stopSpeech = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
    setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
    speechLastCharRef.current = 0;
    speechTextOffsetRef.current = 0;
  };

  // Cancel speech on unmount or page refresh
  useEffect(() => {
    window.addEventListener('beforeunload', stopSpeech);
    return () => { stopSpeech(); window.removeEventListener('beforeunload', stopSpeech); };
  }, []);

  const handleReadAloud = () => {
    if (!window.speechSynthesis) return;

    // ── Pause ─────────────────────────────────────────────────────────────────
    if (speaking && !speechPaused) {
      if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
      if (isIOSSpeech()) {
        // iOS: pause() has no effect — cancel instead and keep position via speechLastCharRef
        window.speechSynthesis.cancel();
      } else {
        window.speechSynthesis.pause();
      }
      setSpeechPaused(true);
      return;
    }

    // ── Non-iOS resume (native browser API) ────────────────────────────────────
    if (speaking && speechPaused && !isIOSSpeech()) {
      window.speechSynthesis.resume();
      setSpeechPaused(false);
      resumeIntervalRef.current = setInterval(() => {
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      }, 10000);
      return;
    }

    // ── Fresh start OR iOS resume — both paths create a new utterance ──────────
    const langCode = story.language || 'en';
    const bcp47 = SPEECH_LANG[langCode] || 'en-GB';
    const langPrefix = bcp47.split('-')[0];
    const lesson = story.moralLesson ? ` Tonight's lesson: ${story.moralLesson}` : '';
    const fullText = (story.title + '. ' + story.content + lesson)
      .replace(/\*\*|__|\*|_/g, '').replace(/\n\n+/g, '. ')
      .replace(/\n/g, ' ').replace(/\.\.+/g, '.').replace(/\s{2,}/g, ' ')
      .replace(/\s+([.,!?])/g, '$1').trim();

    const LANG_NAMES = { en:'English', es:'Spanish', fr:'French', hi:'Hindi', gu:'Gujarati', de:'German', pt:'Portuguese', ar:'Arabic', zh:'Chinese', ja:'Japanese', it:'Italian' };
    const voices = voicesRef.current;
    const voice = voices.length
      ? (voices.find(v => v.lang === bcp47 && v.localService) ||
         voices.find(v => v.lang === bcp47) ||
         voices.find(v => v.lang.startsWith(langPrefix)) ||
         (langCode === 'en' ? voices.find(v => v.lang.startsWith('en')) : null))
      : null;

    if (!voice && langCode !== 'en') {
      const langName = LANG_NAMES[langCode] || langCode.toUpperCase();
      setSpeechError(`Your browser has no ${langName} voice installed. Try on a mobile device for ${langName} read-aloud.`);
      return;
    }
    setSpeechError(null);

    const isFresh = !(speaking && speechPaused); // false only on iOS resume path
    const offset = isFresh ? 0 : speechLastCharRef.current;
    const text = offset > 0 && speechFullTextRef.current ? speechFullTextRef.current.slice(offset) : fullText;

    if (isFresh) {
      // Build paragraph char positions only on fresh start (positions don't change on resume)
      speechFullTextRef.current = fullText;
      speechLastCharRef.current = 0;
      speechTextOffsetRef.current = 0;
      const paragraphs = (story.content || '').split('\n\n');
      const titlePart = story.title + '. ';
      let pos = titlePart.length;
      speechParaPosRef.current = paragraphs.map(p => {
        const start = pos; pos += p.replace(/\*\*|__|\*|_/g, '').length + 2; return start;
      });
      trackSharedStoryReadAloud(langCode);
    }
    speechTextOffsetRef.current = offset;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = bcp47; utterance.rate = playbackSpeed * 0.85; utterance.pitch = 1.05;
    if (voice) utterance.voice = voice;

    utterance.onboundary = (e) => {
      if (e.name !== 'word' && e.name !== 'sentence') return;
      const abs = e.charIndex + speechTextOffsetRef.current;
      speechLastCharRef.current = abs; // keep updated for iOS pause position tracking
      const pp = speechParaPosRef.current;
      let active = -1;
      for (let i = 0; i < pp.length; i++) { if (abs >= pp[i]) active = i; else break; }
      setActiveParagraph(active);
    };
    utterance.onend = () => {
      if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
      setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
      speechLastCharRef.current = 0; speechTextOffsetRef.current = 0;
    };
    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') {
        if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
        setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
      }
    };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    resumeIntervalRef.current = setInterval(() => {
      if (window.speechSynthesis.paused && !speechPaused) window.speechSynthesis.resume();
    }, 10000);
    setSpeaking(true); setSpeechPaused(false);
  };

  const fetchStory = async (pwd = '') => {
    try {
      const res = await API.get(`/shared/${token}${pwd ? `?password=${encodeURIComponent(pwd)}` : ''}`);
      setStory(res.data.story);
      setReactions(res.data.reactions || []);
      setStatus('ok');
      trackSharedStoryView();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 401 && data?.passwordRequired) {
        setStatus('password');
      } else if (err.response?.status === 401) {
        setPasswordError('Incorrect password. Please try again.');
      } else if (err.response?.status === 410 || data?.expired) {
        setStatus('expired');
      } else {
        setStatus('error');
        setErrorMsg(data?.message || 'This link is invalid or has been removed.');
      }
    }
  };

  useEffect(() => { fetchStory(); }, [token]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    fetchStory(password);
  };

  const handleReact = async (emoji) => {
    if (reacted) return;
    setReactingEmoji(emoji);
    try {
      const res = await API.post(`/shared/${token}/react`, { emoji });
      setReactions(res.data.reactions);
      setReacted(true);
      trackSharedStoryReaction(emoji);
    } catch {
      // silent fail
    } finally {
      setReactingEmoji(null);
    }
  };

  // Tally reactions by emoji
  const reactionCounts = reactions.reduce((acc, r) => {
    acc[r.emoji] = (acc[r.emoji] || 0) + 1;
    return acc;
  }, {});

  // ── Password gate ──────────────────────────────────────
  if (status === 'password') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}>
        <div className="card border-0 shadow-lg p-4 mx-3" style={{borderRadius:'20px', maxWidth:'400px', width:'100%'}}>
          <div className="text-center mb-4">
            <div className="fs-2 mb-2">🔒</div>
            <h5 className="fw-bold" style={{color:'var(--text-main)'}}>This story is password protected</h5>
            <p className="text-muted small">Enter the password to read the story.</p>
          </div>
          {passwordError && <div className="alert alert-danger py-2 small">{passwordError}</div>}
          <form onSubmit={handlePasswordSubmit}>
            <input type="password" className="form-control form-control-lg mb-3" placeholder="Enter password"
              value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
            <button type="submit" className="btn btn-dream btn-lg w-100 rounded-pill fw-bold">Unlock Story</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Expired ────────────────────────────────────────────
  if (status === 'expired') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}>
        <div className="text-center text-white px-4">
          <div className="fs-1 mb-3">⏰</div>
          <h4 className="fw-bold mb-2">This link has expired</h4>
          <p className="text-white-50 mb-4">The parent can generate a new link from their Dreamy Bedtime account.</p>
          <Link to="/" className="btn btn-light rounded-pill px-4">Visit Dreamy Bedtime</Link>
        </div>
      </div>
    );
  }

  // ── Error / not found ──────────────────────────────────
  if (status === 'error') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}>
        <div className="text-center text-white px-4">
          <div className="fs-1 mb-3">🌙</div>
          <h4 className="fw-bold mb-2">{errorMsg}</h4>
          <Link to="/" className="btn btn-light rounded-pill px-4 mt-3">Visit Dreamy Bedtime</Link>
        </div>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────
  if (status === 'loading' || !story) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" style={{width:'3rem', height:'3rem'}}></div>
          <p>Loading story...</p>
        </div>
      </div>
    );
  }

  // Build display name — for multi-child stories show all children, else just primary
  const allChildNames = story.isMultiChild && story.multiChildIds?.length
    ? story.multiChildIds.map(c => c.name)
    : [story.child?.name].filter(Boolean);
  const childName = allChildNames.length > 1
    ? allChildNames.slice(0, -1).join(', ') + ' & ' + allChildNames[allChildNames.length - 1]
    : allChildNames[0] || 'a special child';

  // ── Story viewer ───────────────────────────────────────
  return (
    <div style={{minHeight:'100vh', background:'var(--page-bg)'}}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Top bar */}
      <div style={{background:'#2d1b69', padding:'14px 24px'}}>
        <div className="container d-flex align-items-center justify-content-between" style={{maxWidth:'720px'}}>
          <Link to="/" style={{fontFamily:"'Fredoka One',cursive", fontSize:'22px', color:'white', textDecoration:'none'}}>🌙 Dreamy Bedtime</Link>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-light rounded-circle" onClick={() => setFontSize(f => Math.max(14, f - 2))} title="Smaller">A-</button>
            <button className="btn btn-sm btn-outline-light rounded-circle" onClick={() => setFontSize(f => Math.min(26, f + 2))} title="Larger">A+</button>
            <button
              className="btn btn-sm rounded-pill px-3 fw-semibold"
              onClick={() => {
                copyToClipboard(window.location.href).then((ok) => {
                  if (!ok) return;
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
              style={copied ? { background: '#059669', color: '#fff', border: 'none' } : { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.5)' }}
              title="Copy link to share"
            >
              <i className={`bi ${copied ? 'bi-check' : 'bi-share'} me-1`}></i>
              {copied ? 'Copied!' : 'Share'}
            </button>
            <button
              className="btn btn-sm rounded-pill px-3 fw-semibold"
              onClick={handleReadAloud}
              style={speaking && !speechPaused ? { background: '#f59e0b', color: '#fff', border: 'none' }
                : speechPaused ? { background: 'transparent', color: '#fbbf24', border: '1px solid #fbbf24' }
                : { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.5)' }}
              title={speaking && !speechPaused ? 'Pause' : speechPaused ? 'Resume' : 'Read aloud'}
            >
              <i className={`bi ${speaking && !speechPaused ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
              {speaking && !speechPaused ? 'Pause' : speechPaused ? 'Resume' : 'Read Aloud'}
            </button>
            {speaking && (
              <button className="btn btn-sm rounded-pill px-3 fw-semibold" onClick={stopSpeech}
                style={{ background: 'transparent', color: '#fca5a5', border: '1px solid #fca5a5' }}>
                <i className="bi bi-stop-fill me-1"></i>Stop
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container py-5" style={{maxWidth:'720px'}}>

        {speechError && (
          <div className="d-flex align-items-center gap-2 p-3 mb-3 rounded-3" style={{ background: '#fff8ed', border: '1px solid #fcd34d', fontSize: '13px', color: '#92400e' }}>
            <i className="bi bi-info-circle-fill" style={{ flexShrink: 0 }}></i>
            <span style={{ flex: 1 }}>{speechError}</span>
            <button onClick={() => setSpeechError(null)} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>
        )}

        {/* Story card */}
        <div className="card border-0 shadow" style={{borderRadius:'24px', overflow:'hidden'}}>
          {/* Cover strip */}
          <div className="p-4 text-center text-white" style={{background:'linear-gradient(135deg,#2d1b69,#6f42c1)'}}>
            <div style={{fontSize:'52px', marginBottom:'12px'}}>🌙</div>
            <h1 className="fw-bold mb-2" style={{fontFamily:"'Fredoka One',cursive", fontSize:'clamp(22px,5vw,32px)'}}>{story.title}</h1>
            <p style={{color:'#c9a8ff', fontSize:'15px', marginBottom:0}}>A story made just for <strong style={{color:'white'}}>{childName}</strong></p>
          </div>

          {/* Illustration */}
          {story.illustrationUrl && (
            <div>
              <img
                src={story.illustrationUrl}
                alt={story.title}
                style={{width:'100%', display:'block'}}
              />
            </div>
          )}

          {/* Story content */}
          <div className="p-4 p-lg-5" style={{background:'var(--card-bg)'}}>
            {/* Speed controls — shown while narration is active */}
            {(speaking || speechPaused) && (
              <div className="d-flex align-items-center gap-1 mb-3">
                <span style={{fontSize:'12px', color:'var(--text-soft)', marginRight:'4px'}}>Speed:</span>
                {[0.75, 1, 1.25].map(s => (
                  <button key={s} onClick={() => setPlaybackSpeed(s)}
                    className="btn btn-sm rounded-pill px-2 py-0"
                    style={{
                      fontSize:'12px', lineHeight:'1.7',
                      background: playbackSpeed === s ? '#6f42c1' : 'transparent',
                      color: playbackSpeed === s ? '#fff' : '#6f42c1',
                      border: `1px solid ${playbackSpeed === s ? 'transparent' : '#c9a8ff'}`,
                    }}>
                    {s}x
                  </button>
                ))}
              </div>
            )}
            <div style={{lineHeight:'1.9', fontSize:`${fontSize}px`, color:'var(--text-main)'}}>
              {story.content?.split('\n\n').map((para, i) => {
                const isActive = activeParagraph === i && (speaking || speechPaused);
                return (
                  <p key={i} className="mb-4" style={isActive ? {
                    background: 'rgba(111,66,193,0.09)',
                    borderLeft: '3px solid #6f42c1',
                    paddingLeft: '10px',
                    borderRadius: '4px',
                    transition: 'background 0.3s',
                  } : { paddingLeft: 0, borderLeft: 'none' }}>
                    {para}
                  </p>
                );
              })}
            </div>

            {/* Moral lesson */}
            {story.moralLesson && (
              <div className="mt-4 p-4 text-center rounded-3" style={{background:'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border:'2px dashed #c9a8ff'}}>
                <div style={{fontSize:'32px', marginBottom:'8px'}}>💫</div>
                <p className="fw-bold mb-1" style={{color:'var(--text-main)'}}>Tonight's lesson</p>
                <p className="mb-0 fst-italic" style={{color:'#6f42c1', fontSize:'17px'}}>"{story.moralLesson}"</p>
              </div>
            )}
          </div>

          {/* Reactions */}
          <div className="p-4 text-center border-top" style={{background:'var(--header-bg)'}}>
            {reacted ? (
              <div>
                <div style={{fontSize:'36px', marginBottom:'8px'}}>🥰</div>
                <p className="fw-semibold mb-0" style={{color:'var(--text-main)'}}>Thank you! {childName}'s parent will love knowing you enjoyed the story.</p>
              </div>
            ) : (
              <>
                <p className="fw-semibold mb-3" style={{color:'var(--text-main)'}}>How did you like the story?</p>
                <div className="d-flex justify-content-center gap-3">
                  {['❤️', '🌟', '😄'].map(emoji => (
                    <button key={emoji} className="btn border-0 p-0" style={{fontSize:'42px', lineHeight:1, background:'none'}}
                      onClick={() => handleReact(emoji)} disabled={!!reactingEmoji}>
                      {reactingEmoji === emoji
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : emoji}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Running reaction totals */}
            {Object.keys(reactionCounts).length > 0 && (
              <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
                {Object.entries(reactionCounts).map(([e, count]) => (
                  <span key={e} className="badge rounded-pill px-3 py-2" style={{background:'var(--purple-section)', color:'#c9a8ff', fontSize:'14px'}}>
                    {e} {count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-5 pb-4">
          <p className="text-muted small mb-2">Want to create magical bedtime stories for your grandchildren?</p>
          <Link to="/register" className="btn btn-dream rounded-pill px-4 fw-semibold">
            Try Dreamy Bedtime Free
          </Link>
          <p className="text-muted mt-3" style={{fontSize:'12px'}}>5 free stories every month · No credit card needed</p>
        </div>
      </div>
    </div>
  );
};

export default SharedStoryPage;
